import { useEffect, useState } from 'react'
import { farmerApi, type Delivery, type FarmerProfile } from '../lib/farmer'
import { dateTime, maskedPhone, money, num, shortDate, titleCase } from '../lib/format'
import { Banner, Empty, Loading, Panel, Pill, StatMini } from '../ui/kit'
import { FarmerHeader } from './FarmerShell'

/**
 * The record the farmer never had: what was weighed, by whom, on which certified
 * scale, against which written grade, and what was paid. Without this a farmer has
 * only our word for all of it.
 */
export function MyDeliveries() {
  const [rows, setRows] = useState<Delivery[]>([])
  const [me, setMe] = useState<FarmerProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      farmerApi.get<Delivery[]>('/portal/deliveries'),
      farmerApi.get<FarmerProfile>('/portal/me'),
    ])
      .then(([d, profile]) => { setRows(d); setMe(profile) })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  const unpaid = rows.filter((r) => r.paymentStatus && r.paymentStatus !== 'confirmed')

  return (
    <>
      <FarmerHeader />
      <div className="field-greet">
        <h1>Payments</h1>
        <p>Every collection, what it weighed, and what you were paid for it.</p>
      </div>

      <div className="field-body">
        {loading && <Panel><Loading rows={4} /></Panel>}

        {me && (
          <div className="mini-row">
            <StatMini label="Collections" value={me.totals.lots} />
            <StatMini label="Total sold" value={`${num(me.totals.kg, 0)} kg`} />
            <StatMini label="Total paid" value={money(me.totals.paid, { compact: true })} />
          </div>
        )}

        {me?.payoutAccount && (
          <div className="payout">
            <span className="payout-mark">M</span>
            <div className="payout-who">
              <b>{titleCase(me.payoutAccount.channel)}</b>
              <i>{maskedPhone(me.payoutAccount.accountRef)} · {me.payoutAccount.accountName}</i>
            </div>
            <div className="payout-amt">
              <span style={{ color: me.payoutAccount.nameMatchState === 'match' ? 'var(--ok)' : 'var(--warn)' }}>
                {me.payoutAccount.nameMatchState === 'match' ? 'name checked ✓' : 'name not checked'}
              </span>
            </div>
          </div>
        )}

        {unpaid.length > 0 && (
          <Banner tone="warn" title={`${unpaid.length} payment${unpaid.length === 1 ? '' : 's'} still on the way`}>
            Payments are sent the same day, before the 13:00 cut-off. If one has not arrived by
            then, tell your agent.
          </Banner>
        )}

        {!loading && rows.length === 0 && (
          <Empty title="Nothing collected yet">
            Once an agent weighs a harvest from you, it appears here with the weight, the scale it
            was weighed on, and what you were paid.
          </Empty>
        )}

        {rows.length > 0 && (
          <Panel title="Collections" meta={`${rows.length}`}>
            <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {rows.map((r) => {
                const isOpen = open === r.lotCode
                const paid = r.paymentStatus === 'confirmed'
                return (
                  <div key={r.lotCode}>
                    <button
                      className="stop-row"
                      onClick={() => setOpen(isOpen ? null : r.lotCode)}
                      style={{ width: '100%' }}
                    >
                      <span className={`stop-state ${paid ? 'done' : 'pending'}`}>
                        {paid ? '✓' : '·'}
                      </span>
                      <span className="stop-who">
                        <b>{r.cropName} · {num(r.weighedKg, 1)} kg</b>
                        <i>{shortDate(r.collectedAt)} · grade {r.gradeCode} at {money(r.pricePerKg, { currency: false })}/kg</i>
                      </span>
                      <span className="stop-amt">
                        <b className={paid ? 'ok-t' : ''}>{money(r.amount, { currency: false })}</b>
                        <span>{paid ? 'paid' : titleCase(r.paymentStatus ?? 'not raised')}</span>
                      </span>
                    </button>

                    {isOpen && (
                      <div style={{
                        background: 'var(--surface-2)', border: '1px solid var(--line-2)',
                        borderRadius: 'var(--r-md)', padding: 'var(--sp-6) var(--sp-7)',
                        margin: '0 0 var(--sp-6)',
                      }}>
                        <div className="micro" style={{ marginBottom: 'var(--sp-5)' }}>
                          How this was worked out
                        </div>
                        <Line label="Weighed" value={`${num(r.weighedKg, 1)} kg`} />
                        <Line label="Price for grade " suffix={r.gradeCode}
                          value={`${money(r.pricePerKg, { currency: false })} / kg`} />
                        <Line label="Paid" value={money(r.amount)} strong />
                        <div className="divider" style={{ margin: 'var(--sp-5) 0' }} />
                        <Line label="Weighed by" value={r.weighedBy ?? '—'} />
                        <Line label="On scale" value={r.scaleCode ?? '—'} />
                        {r.scaleCertificate && (
                          <Line label="Scale certificate" value={r.scaleCertificate} />
                        )}
                        <Line label="When" value={dateTime(r.weighedAt ?? r.collectedAt)} />
                        {r.receipt && <Line label="M-Pesa receipt" value={r.receipt} />}
                        {r.paidAt && <Line label="Paid at" value={dateTime(r.paidAt)} />}
                        <div className="divider" style={{ margin: 'var(--sp-5) 0' }} />
                        <div className="micro" style={{ marginBottom: 4 }}>Grade {r.gradeCode} means</div>
                        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-2)', lineHeight: 1.55 }}>
                          {r.gradeSpec}
                        </p>
                        <div style={{ marginTop: 'var(--sp-5)' }}>
                          <Pill dot={false}>Lot {r.lotCode}</Pill>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Panel>
        )}

      </div>
    </>
  )
}

function Line({ label, value, suffix, strong }: {
  label: string; value: string; suffix?: string; strong?: boolean
}) {
  return (
    <div className="spread" style={{ fontSize: 'var(--fs-sm)', padding: '3px 0' }}>
      <span className="muted">{label}{suffix}</span>
      <b className="num" style={{ fontWeight: strong ? 800 : 600 }}>{value}</b>
    </div>
  )
}
